import { extractApiErrorMessage } from '../services/utils/apiErrorUtils';

/** Pedidos PATCH de impedimento em voo ao mesmo tempo. */
export const SUSPENSION_TRACE_CONCURRENCY = 4;

export type SuspensionTraceBatchResult = { ok: true } | { ok: false };

type Settled =
  | { ok: true }
  | { ok: false; error: unknown };

async function mapSettledWithConcurrency<T>(
  items: T[],
  concurrency: number,
  mapper: (item: T) => Promise<void>
): Promise<Settled[]> {
  if (items.length === 0) return [];

  const results = new Array<Settled>(items.length);
  const limit = Math.max(1, Math.min(concurrency, items.length));
  let nextIndex = 0;

  const worker = async () => {
    while (true) {
      const current = nextIndex;
      nextIndex += 1;
      if (current >= items.length) return;
      try {
        await mapper(items[current]);
        results[current] = { ok: true };
      } catch (error) {
        results[current] = { ok: false, error };
      }
    }
  };

  await Promise.all(Array.from({ length: limit }, () => worker()));
  return results;
}

/**
 * Atualiza o impedimento de várias linhas com fila curta.
 * Falha total devolve `{ ok: false }` (o diálogo permanece aberto) e mostra a primeira
 * mensagem real do PATCH. Falha parcial só notifica, sem rejeitar a promise.
 */
export async function runSuspensionTraceBatch(options: {
  lineIds: string[];
  updateLine: (lineId: string) => Promise<void>;
  notifyError: (message: string) => void;
  notifySuccess: (message: string) => void;
}): Promise<SuspensionTraceBatchResult> {
  const { lineIds, updateLine, notifyError, notifySuccess } = options;
  const settled = await mapSettledWithConcurrency(
    lineIds,
    SUSPENSION_TRACE_CONCURRENCY,
    updateLine
  );

  const failures = settled.filter((item): item is { ok: false; error: unknown } => !item.ok);
  const updated = lineIds.length - failures.length;

  if (updated === 0) {
    notifyError(
      extractApiErrorMessage(
        failures[0]?.error,
        'Não foi possível atualizar o impedimento nas linhas selecionadas.'
      )
    );
    return { ok: false };
  }

  if (failures.length > 0) {
    notifyError(`${updated} linha(s) atualizada(s); ${failures.length} falhou(ram).`);
  } else {
    notifySuccess(
      updated === 1 ? 'Impedimento atualizado.' : `Impedimento registado em ${updated} linhas.`
    );
  }

  return { ok: true };
}
